/**
 * @fileoverview `EventDecisionBar` (WEB-708 tablet · WEB-709 mobile) — the two
 * narrow-band renderings of the Event v2 decision payload.
 *
 * ## Why the decision cannot simply stay put
 *
 * Below 1024px `--evt-grid` collapses to a single column, so the right rail
 * the sticky card lives in has nowhere left to stick — the decision would
 * simply fall to the bottom of a long document, which is the one place a
 * reader halfway through a lineup will not look. The design's answer is to
 * relocate the decision section to a bar: pinned under the page chrome at
 * tablet, pinned to the bottom of the viewport at mobile. The card itself
 * stays (WEB-762) so the M3 satellites keep their boxes.
 *
 * ```
 * ≥1024   EventDecisionPanelView   decision section in the panel card
 * 768–1023 variant="bar"           sticky top: var(--nav-stick-top), hairline BOTTOM border
 * ≤767    variant="bottom-bar"     sticky bottom: 0,  hairline TOP border
 * ```
 *
 * ## One payload, one CTA, three renderings
 *
 * All three read the same `EventDetailV2.decisionPanel` object and share
 * `decisionStyles.ts` for the CTA appearance and the honest-absence strings,
 * so there is exactly one source of the CTA href, the note, and "Time TBA".
 * Nothing here formats a date, a time, or a price — the server already did
 * (API-1663).
 *
 * ## Visibility is CSS, never JS
 *
 * This component sets NO `display` of its own and reads no viewport. Which
 * rendering is live is decided entirely by the band rules in `eventSpine.css`,
 * off the same 768/1024 breakpoints as the WEB-702 size ladder — so the server
 * render and the hydrated client render agree byte-for-byte with no
 * measurement pass and no `useMediaQuery` branch.
 *
 * Those rules use `display: none` on the inactive renderings (never
 * `visibility` and never opacity), which is what keeps the two hidden ones out
 * of the accessibility tree: at any width there is exactly ONE element
 * carrying the CTA's accessible name, so a screen reader never announces two
 * "Get tickets" controls.
 *
 * ## Stickiness lives in the stylesheet too
 *
 * `position: sticky` is declared on `.event-spine__bar--tablet` /
 * `.event-spine__bar--mobile`, not here — same division of labour as the card,
 * whose stickiness belongs to `.event-spine__panel`. Both bars mount as direct
 * children of `.event-spine` (see `EventSpineTemplate`), which is their
 * containing block; the mobile bar therefore stays pinned to the bottom of the
 * viewport for the whole length of the spine and comes to rest at the spine's
 * own end, which is the behaviour the issue specifies (sticky, not fixed).
 *
 * WEB-709 explicitly defers a scroll-retreat animation to a follow-up: neither
 * bar animates in or out.
 *
 * ## Client island
 *
 * Same reason as the card: the CTA anchor wires an optional `onCtaActivate`
 * click handler, and an event-handler prop can only cross into a Client
 * Component. `EventSpineTemplate` itself stays a Server Component.
 */
'use client';

import * as React from 'react';
import { ArrowUpRight } from 'lucide-react';

import { cn } from '@/components/atoms/primitives/utils';
import { getEventLexicon } from '@/config/eventLexicon';
import { isLongCtaLabel } from '@/lib/eventDetail/resale';
import { EVT_MICRO_LABEL_10_MONO_CLASS } from '@/lib/eventV2MicroLabel';
import { normalizeMeridiem } from '@/lib/format/normalizeMeridiem';
import type {
  EventDecisionPanel as EventDecisionPanelPayload,
  EventDetailV2,
} from '@/types/eventDetail';

import {
  DECISION_CTA_DISABLED_CLASS,
  DECISION_CTA_LINK_CLASS,
  DECISION_CTA_MIN_HEIGHT_CLASS,
  HEADLINE_FALLBACK,
  resolveDecisionCtaNote,
  shouldHideDisabledDecisionCta,
} from './decisionStyles';

/**
 * `bar` = the 768–1023 strip under the page chrome (WEB-708).
 * `bottom-bar` = the ≤767 strip pinned to the bottom of the viewport (WEB-709).
 */
export type EventDecisionBarVariant = 'bar' | 'bottom-bar';

/** Maps a variant onto the band class `eventSpine.css` keys its rules off. */
const VARIANT_BAND_CLASS: Record<EventDecisionBarVariant, string> = {
  bar: 'event-spine__bar--tablet',
  'bottom-bar': 'event-spine__bar--mobile',
};

export interface EventDecisionBarViewProps {
  /** Which band this rendering belongs to — see {@link EventDecisionBarVariant}. */
  variant: EventDecisionBarVariant;
  /**
   * The server-assembled payload (`EventDetailV2.decisionPanel`). `undefined`
   * means the server could not assemble an honest panel — the bar then shows
   * the degraded lede only (lexicon kicker + "Time TBA") and no CTA, exactly
   * as the card does.
   */
  decisionPanel: EventDecisionPanelPayload | undefined;
  /**
   * `EventLexiconEntry.panelKicker` ("Doors" / "Gates" / "Seating") — used in
   * the degraded state, where there is no server kicker string to show.
   * Ignored when `decisionPanel` is present; the server's own `kicker` wins.
   */
  fallbackKicker: string;
  /** Fired once when the user activates a usable (non-disabled) CTA link. */
  onCtaActivate?: () => void;
  /** Additional class names on the bar root. */
  className?: string;
}

/**
 * `EventDecisionBarView` — pure-props presentational View, the target of
 * Storybook and the unit tests. See the file header for the render contract.
 */
export function EventDecisionBarView({
  variant,
  decisionPanel,
  fallbackKicker,
  onCtaActivate,
  className,
}: EventDecisionBarViewProps): React.ReactElement {
  const isBottom = variant === 'bottom-bar';

  const kickerText = decisionPanel ? decisionPanel.kicker : fallbackKicker;
  const headlineText = normalizeMeridiem(decisionPanel?.headline ?? HEADLINE_FALLBACK);
  const subline = decisionPanel ? decisionPanel.subline : null;

  const cta = decisionPanel?.cta ?? null;
  const note = resolveDecisionCtaNote(cta);
  const hideDisabledCta = shouldHideDisabledDecisionCta(cta);

  // The second line differs by band, on purpose, because the bands have
  // different amounts of room and different questions to answer:
  //
  //   tablet — "the sub-line beside it": the full day/date/end-time context,
  //            with the price note after it when there is one.
  //   mobile — "the price note truncated to one line": at 375px the price is
  //            the decision-relevant fact, so it leads. `subline` is the
  //            fallback rather than dead space when the server sent no note
  //            (both are real server strings — neither is fabricated).
  //
  // A disabled CTA's explanation rides `note` in both bands, so it is never
  // the thing that gets dropped.
  const metaItems = (isBottom ? [note ?? subline] : [subline, note])
    .filter((part): part is string => typeof part === 'string' && part.trim().length > 0)
    // A server that puts the same string in `subline` and `cta.note` should
    // not make the tablet bar say it twice.
    .filter((part, index, all) => all.indexOf(part) === index);

  return (
    <div
      className={cn('event-spine__bar', VARIANT_BAND_CLASS[variant], className)}
      data-testid="event-decision-bar"
      data-variant={variant}
      // WEB-1098 — DS 1.10.0 option (c): a CTA label over 12 characters stacks
      // the ≤767 bar (`eventSpine.css`). Derived from the label string, never
      // a measurement, so SSR and hydration agree. Absent without a CTA.
      data-cta-long={cta && !hideDisabledCta ? String(isLongCtaLabel(cta.label)) : undefined}
    >
      <div className="event-spine__bar-summary" data-testid="event-decision-bar-summary">
        <p
          className={cn('event-spine__bar-lede', EVT_MICRO_LABEL_10_MONO_CLASS, 'text-fg-2')}
          data-testid="event-decision-bar-lede"
        >
          <span data-testid="event-decision-bar-kicker">{kickerText}</span>
          {/* Decorative separator: `aria-hidden` so the accessible name of the
              lede reads "Doors 7:00 pm", not "Doors middot 7:00 pm". */}
          <span aria-hidden="true" className="px-1.5 text-fg-3">
            ·
          </span>
          <span
            data-testid="event-decision-bar-headline"
            className="text-small font-bold tracking-normal text-fg-1 font-display normal-case"
          >
            {headlineText}
          </span>
        </p>

        {metaItems.length > 0 ? (
          <p
            className={cn('event-spine__bar-meta', EVT_MICRO_LABEL_10_MONO_CLASS, 'text-fg-3')}
            data-testid="event-decision-bar-meta"
          >
            {metaItems.map((item, index) => (
              <React.Fragment key={item}>
                {index > 0 ? (
                  <span aria-hidden="true" className="px-1.5">
                    ·
                  </span>
                ) : null}
                <span>{item}</span>
              </React.Fragment>
            ))}
          </p>
        ) : null}
      </div>

      {/* CTA — only when a real payload exists. Absent payload => no CTA at all,
          matching the card rather than offering a control the page cannot honour. */}
      {cta && !hideDisabledCta ? (
        cta.href !== null ? (
          <a
            href={cta.href}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="event-decision-bar-cta"
            data-state="enabled"
            data-cta-state="active"
            onClick={onCtaActivate}
            className={cn(
              'event-spine__bar-cta',
              DECISION_CTA_LINK_CLASS,
              DECISION_CTA_MIN_HEIGHT_CLASS,
              'px-4 text-body',
            )}
          >
            <span>{cta.label}</span>
            <ArrowUpRight
              aria-hidden="true"
              className={cn(
                'size-[15px] shrink-0 transition-transform duration-standard',
                'group-hover:-translate-y-0.5 group-hover:translate-x-0.5 motion-reduce:transition-none',
              )}
            />
          </a>
        ) : (
          // Honest disabled state: inert, never a dead `#` link. A bare,
          // tabindex-less <div> is not in the tab order. Its explanation is the
          // `note` already rendered in the meta line above. A missing link with
          // no server note hides this control (WEB-1190 D6).
          <div
            data-testid="event-decision-bar-cta"
            aria-disabled="true"
            data-state="disabled"
            data-cta-state="disabled"
            className={cn(
              'event-spine__bar-cta',
              DECISION_CTA_DISABLED_CLASS,
              DECISION_CTA_MIN_HEIGHT_CLASS,
              'px-4 text-body',
            )}
          >
            <span>{cta.label}</span>
          </div>
        )
      ) : null}
    </div>
  );
}

export interface EventDecisionBarProps {
  /** Event detail payload from `fetchEventDetail` (WEB-197, API-1404). */
  event: EventDetailV2;
  /** Which band this rendering belongs to. */
  variant: EventDecisionBarVariant;
  /**
   * Category slug driving the degraded-state lexicon kicker. Pass the same
   * slug `EventSpineTemplate` and the card resolve from, so no two renderings
   * of the same page disagree about the kicker word.
   */
  categorySlug?: string | null;
  onCtaActivate?: () => void;
  className?: string;
}

/**
 * `EventDecisionBar` — Connected. Derives `decisionPanel` and the lexicon
 * fallback kicker from the already-fetched `EventDetailV2`, exactly as
 * `EventDecisionPanel` does. No RTK Query hook: the payload rides on the event
 * detail response the route already fetched (API-1661/API-1663).
 */
export function EventDecisionBar({
  event,
  variant,
  categorySlug,
  onCtaActivate,
  className,
}: EventDecisionBarProps): React.ReactElement {
  const slug = typeof categorySlug === 'string' ? categorySlug.trim() : '';
  const lexicon = getEventLexicon(slug);

  return (
    <EventDecisionBarView
      variant={variant}
      decisionPanel={event.decisionPanel}
      fallbackKicker={lexicon.panelKicker}
      onCtaActivate={onCtaActivate}
      className={className}
    />
  );
}
